// Smart swap router - prioritizes aggregators (MM2 paused)
// MM2 Imports - PAUSED (can be re-enabled in future)
// import { getMM2SwapQuote, executeMM2Swap, getMM2SwapStatus } from './mm2Client';
// import { getMM2RuntimeState, isMM2RouteAvailable } from './mm2Runtime';
import {
  // fetch0xQuote, // Temporarily disabled - API-side configuration issue
  // fetchParaswapQuote, // DISABLED until fee collection configured
  // fetchKyberSwapQuote, // DISABLED until fee collection configured
  // fetchOpenOceanQuote, // DISABLED until fee collection configured
  fetch1inchQuote, // ✅ ACTIVE - Can collect fees via smart contract
  // fetchRavencoinBridgeQuote, // REMOVED - Not usable
  // fetchWormholeQuote, // DISABLED until fee collection configured
  fetchAcrossQuote,
  fetchSimpleSwapQuote, // ✅ ACTIVE - Affiliate commission
  fetchChangeNOWQuote,
  fetchICPChainFusionQuote,
  isICPChainFusionAvailable,
  fetchICPDEXQuote,
  isICPDEXAvailable,
  executeProviderSwap,
  type SwapQuote,
} from './liquidityProviders';
import { toast } from 'sonner';
import { routeBTCToUSDT, isBTCToUSDTAvailable } from './btcToUsdtRouter';
import { 
  isCrossChainSwap, 
  getTokenChains, 
  getTokenPrimaryChain,
  CHAIN_IDS 
} from './tokenRegistry';

export interface RouterQuote extends SwapQuote {
  source: 'mm2' | 'aggregator';
  fromChainId?: number; // Source chain ID (numeric)
  toChainId?: number; // Destination chain ID (numeric)
  pathway?: string; // Pathway description (e.g., "BTC → WBTC → USDC")
  requiresWrapping?: boolean; // Whether this route requires wrapping native UTXO
  wrappingToken?: string; // Wrapped token used in the pathway
  wrappingInstructions?: string; // Instructions for wrapping
}

export interface RouterResult {
  best: RouterQuote | null;
  all: RouterQuote[];
  mm2Available: boolean;
}

/**
 * Smart routing: Try MM2 first (atomic swaps), then aggregators
 * Returns best quote and all available quotes
 */
export async function getOptimalSwapRoute(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1,
  destinationAddress?: string,
  customTokenPairs?: Array<{ symbol: string; name: string; contractAddress?: string | null; network?: string | null }>,
  fromTokenChainId?: number,
  toTokenChainId?: number
): Promise<RouterResult> {
  // Tier 0: Import utilities needed for routing decisions
  const { isAtomicSwapCompatible, hasWrappedVersion } = await import('./atomicSwapUtils');
  
  // Detect cross-chain swap FIRST (before trying WBTC routing)
  // This allows direct BTC swaps (SimpleSwap/ChangeNOW) to be checked before wrapped token routing
  const fromChains = getTokenChains(fromToken, customTokenPairs);
  const toChains = getTokenChains(toToken, customTokenPairs);
  const isCrossChain = isCrossChainSwap(fromToken, toToken, undefined, undefined, customTokenPairs);
  
  // For cross-chain UTXO → EVM swaps (e.g., BTC → USDT, BTC → ETH), prefer direct swaps
  // SimpleSwap and ChangeNOW support direct BTC swaps without needing WBTC
  // Only use WBTC routing as a fallback if direct swaps aren't available
  const fromTokenUpper = fromToken.toUpperCase();
  const toTokenUpper = toToken.toUpperCase();
  const isUTXOFrom = fromTokenUpper === 'BTC' || fromTokenUpper === 'LTC' || 
     fromTokenUpper === 'DOGE' || fromTokenUpper === 'XRP';
  const isEVMTo = !isAtomicSwapCompatible(toToken);
  const isUTXOToEVMCrossChain = isCrossChain && isUTXOFrom && isEVMTo;
  
  // Always log routing decisions (not just in DEV) to debug production issues
  console.log('[Router] Routing decision:', { 
    fromToken, 
    toToken, 
    isCrossChain, 
    isUTXOFrom, 
    isEVMTo, 
    isUTXOToEVMCrossChain,
    fromChains,
    toChains
  });
  if (isUTXOToEVMCrossChain) {
    console.log('[Router] ✅ Skipping WBTC routing - using direct cross-chain swaps');
  }
  
  // Tier 0: Native UTXO → EVM token routing (via wrapped tokens)
  // This handles: BTC→WBTC (other wrapped tokens removed)
  // ONLY use this for same-chain swaps or as fallback after direct cross-chain swaps fail
  // For cross-chain swaps, direct aggregators (SimpleSwap/ChangeNOW) are preferred
  const { isEVMCompatibleToken } = await import('./liquidityProviders');
  const { isUTXOToEVMAvailable, routeUTXOToEVM } = await import('./utxoToEVMRouter');
  
  // Check if token has wrapped version (includes XRP, BTC, LTC, DOGE)
  const hasWrapped = hasWrappedVersion(fromToken);
  const isEVMToken = !isAtomicSwapCompatible(toToken) && isEVMCompatibleToken(toToken, chainId);
  
  // Skip WBTC routing for cross-chain swaps - let direct aggregators handle it first
  // WBTC routing will be used as fallback if direct swaps fail
  // IMPORTANT: For BTC → USDT, BTC → ETH, etc., we want direct swaps, not WBTC routing
  if (hasWrapped && isEVMToken) {
    // ALWAYS skip WBTC routing for cross-chain UTXO → EVM swaps
    // This ensures SimpleSwap/ChangeNOW handle BTC → USDT directly
    if (isUTXOToEVMCrossChain) {
      console.log('[Router] ✅ SKIPPING WBTC routing - using direct cross-chain swaps', { 
        fromToken, 
        toToken, 
        isCrossChain, 
        isUTXOFrom, 
        isEVMTo 
      });
      // Don't call UTXO router - let cross-chain aggregators handle it
      // Continue to cross-chain section below
    } else {
      console.log('[Router] ⚠️ Using WBTC routing (same-chain or fallback)', { fromToken, toToken, isCrossChain, isUTXOToEVMCrossChain, isUTXOFrom, isEVMTo });
      // Determine target chain (use destination token's primary chain or provided chainId)
      const toChains = getTokenChains(toToken, customTokenPairs);
      const targetChainId = toChains.length > 0 ? toChains[0] : chainId;
      
      // Check if wrapped version exists and route is available
      if (isUTXOToEVMAvailable(fromToken, toToken, targetChainId)) {
        try {
          const utxoRoute = await routeUTXOToEVM(fromToken, toToken, amount, targetChainId);
          
          // Return integrated quote showing the full pathway
          // The pathway information is already included in utxoRoute
          return {
            best: utxoRoute.best,
            all: utxoRoute.all,
            mm2Available: false,
          };
        } catch (error) {
          // If routing fails, continue to normal flow
          if (import.meta.env.DEV) {
            console.log('UTXO → EVM routing failed, trying normal flow:', error);
          }
        }
      }
    }
  }
  
  // REMOVED: Special BTC → USDT handler that forced WBTC routing
  // This was preventing direct BTC → USDT swaps via SimpleSwap/ChangeNOW
  // Direct cross-chain aggregators are now checked first in the cross-chain section below
  
  // Check if this is a UTXO swap (used in multiple places)
  const isUTXOSwap = isAtomicSwapCompatible(fromToken) || isAtomicSwapCompatible(toToken);
  
  // Determine source and destination chain IDs
  // Use provided chain IDs if available (from multi-chain token selection), otherwise calculate
  let fromChainId = fromTokenChainId || chainId;
  let toChainId = toTokenChainId || chainId;
  
  if (isCrossChain) {
    // For cross-chain, use provided chain IDs if available, otherwise use primary chains or first available
    if (!fromTokenChainId) {
      fromChainId = getTokenPrimaryChain(fromToken, customTokenPairs) || fromChains[0] || chainId;
    }
    if (!toTokenChainId) {
      toChainId = getTokenPrimaryChain(toToken, customTokenPairs) || toChains[0] || chainId;
    }
    
    // If both tokens are on the provided chainId, use it (unless chain IDs were explicitly provided)
    if (!fromTokenChainId && fromChains.includes(chainId)) {
      fromChainId = chainId;
    }
    if (!toTokenChainId && toChains.includes(chainId)) {
      toChainId = chainId;
    }
  }
  
  let mm2Available = false;
  const allQuotes: RouterQuote[] = [];
  // MM2 Runtime - PAUSED (can be re-enabled in future)
  // const runtime = getMM2RuntimeState();
  // const mm2Allowed = isMM2RouteAvailable();

  try {
    // Tier 1: MM2 (PAUSED - can be re-enabled in future)
    // MM2 atomic swaps are currently disabled to focus on aggregator-based swaps
    // for better RVN swap support via StealthEX/ChangeNOW integration
    /*
    if (mm2Allowed && !isCrossChain) {
      try {
        const mm2Quote = await getMM2SwapQuote(fromToken, toToken, amount);
        
        if (mm2Quote && mm2Quote.estimatedOutput > 0) {
          mm2Available = true;
          const mm2RouterQuote: RouterQuote = {
            provider: 'MM2 (Atomic Swap)',
            rate: mm2Quote.price,
            fee: 0, // MM2 has no fees (just network fees)
            estimatedOutput: mm2Quote.estimatedOutput,
            available: true,
            source: 'mm2',
            fromChainId: fromChainId,
            toChainId: toChainId,
          };
          allQuotes.push(mm2RouterQuote);
          
          // MM2 is best - return immediately
          return {
            best: mm2RouterQuote,
            all: [mm2RouterQuote],
            mm2Available: true,
          };
        }
      } catch (error) {
        // MM2 not available or no liquidity - continue to aggregators
        if (import.meta.env.DEV) {
          console.log('MM2 not available, trying aggregators...', error);
        }
      }
    } else if (runtime.mode === 'default' && !isCrossChain) {
      toast.warning('Atomic swaps disabled', {
        description: 'Unlock your embedded MM2 wallet or point to your own node to use trustless swaps.',
        duration: 6000,
      });
    }
    */

    // Tier 2: Cross-chain bridges (Wormhole, Across, Ravencoin Bridge)
    if (isCrossChain) {
      // isUTXOSwap is already declared above at function level
      const crossChainQuotesPromises: Promise<SwapQuote>[] = [];
      
      // Only call EVM-only bridges (Across) if NOT a UTXO swap
      if (!isUTXOSwap) {
        // Only include Across if integratorId is available
        const acrossIntegratorId = import.meta.env.VITE_ACROSS_INTEGRATOR_ID;
        
        // Wormhole removed - DISABLED until fee collection configured
        
        // Only add Across if integratorId is set
        if (acrossIntegratorId) {
          crossChainQuotesPromises.push(
            fetchAcrossQuote(fromToken, toToken, amount, fromChainId, toChainId)
          );
        }
      }
      
      // Ravencoin Bridge - DISABLED until fee collection configured
      // Removed from cross-chain quotes as it's commented out in liquidityProviders.ts
      /*
      if (isUTXOSwap || fromToken.toUpperCase() === 'RVN' || toToken.toUpperCase() === 'RVN') {
        crossChainQuotesPromises.push(
          fetchRavencoinBridgeQuote(fromToken, toToken, amount)
        );
      }
      */
      
      // ICP Chain Fusion for chain-key token conversions (BTC <-> ckBTC, ETH <-> ckETH)
      if (isICPChainFusionAvailable(fromToken, toToken)) {
        crossChainQuotesPromises.push(
          fetchICPChainFusionQuote(fromToken, toToken, amount)
        );
      }
      
      // SimpleSwap and ChangeNOW support 1,500+ tokens across 50+ blockchains (UTXO + EVM)
      // Query them for ALL cross-chain swaps - let them return unavailable if pair not supported
      // SimpleSwap supports BTC, ETH, USDT, USDC, RVN, XRP, SOL, XMR, LTC, DOGE, and hundreds more
      // ChangeNOW supports 600+ tokens across 110+ blockchains (requires VPS proxy)
      crossChainQuotesPromises.push(
        fetchSimpleSwapQuote(fromToken, toToken, amount, destinationAddress, fromTokenChainId, toTokenChainId) // ✅ ACTIVE - Supports ALL tokens
      );
      
      // Skip ChangeNOW for TON swaps - ChangeNOW has strict address validation that requires properly checksummed TON addresses
      // SimpleSwap works fine for TON, so we use that as the primary provider
      if (fromToken.toUpperCase() !== 'TON' && toToken.toUpperCase() !== 'TON') {
        crossChainQuotesPromises.push(
          fetchChangeNOWQuote(fromToken, toToken, amount, destinationAddress, fromTokenChainId, toTokenChainId) // ✅ ACTIVE - Requires VPS proxy (fixed)
        );
      }
      
      if (crossChainQuotesPromises.length > 0) {
        const crossChainQuotes = await Promise.allSettled(crossChainQuotesPromises);
        
        // Include ALL cross-chain quotes (available + unavailable) so users can see all options
        const allCrossChainQuotes: RouterQuote[] = crossChainQuotes
          .filter((result): result is PromiseFulfilledResult<SwapQuote> => 
            result.status === 'fulfilled'
          )
          .map(result => ({
            ...result.value,
            source: 'aggregator' as const,
            fromChainId: fromChainId,
            toChainId: toChainId,
          }));
        
        // Filter only available quotes for best quote selection
        const availableCrossChainQuotes = allCrossChainQuotes.filter(q => q.available && q.estimatedOutput > 0);
        
        // Add ALL quotes (available + unavailable) so users can see all options
        allQuotes.push(...allCrossChainQuotes);
        
        // If we have cross-chain quotes, prioritize them
        if (availableCrossChainQuotes.length > 0) {
          const bestCrossChainQuote = availableCrossChainQuotes.reduce((best, current) =>
            current.estimatedOutput > best.estimatedOutput ? current : best
          );
          
          toast.info('Cross-Chain Route Found', {
            description: `Using ${bestCrossChainQuote.provider} for cross-chain swap`,
            duration: 4000,
          });
          
          return {
            best: bestCrossChainQuote,
            all: allQuotes,
            mm2Available: false,
          };
        }
      }
    }

    // Tier 3: Same-chain aggregators (DEX liquidity)
    // Only use same-chain aggregators if not cross-chain or if cross-chain failed
    if (!isCrossChain || allQuotes.length === 0) {
      // isUTXOSwap is already declared above at function level
      // Build list of aggregator quote promises, filtering by provider capabilities
      const aggregatorQuotePromises: Promise<SwapQuote>[] = [];
      
      // Only call EVM aggregators if not a UTXO swap
      if (!isUTXOSwap) {
        aggregatorQuotePromises.push(
          // fetch0xQuote(fromToken, toToken, amount, chainId), // Temporarily disabled - API-side configuration issue
          // fetchParaswapQuote(fromToken, toToken, amount, chainId), // DISABLED until fee collection configured
          // fetchKyberSwapQuote(fromToken, toToken, amount, chainId), // DISABLED until fee collection configured
          // fetchOpenOceanQuote(fromToken, toToken, amount, chainId), // DISABLED until fee collection configured
          fetch1inchQuote(fromToken, toToken, amount, chainId) // ✅ ACTIVE - Only EVM aggregator with fee collection
        );
      }
      
      // Ravencoin Bridge - DISABLED until fee collection configured
      // Removed from aggregator quotes as it's commented out in liquidityProviders.ts
      /*
      if (isUTXOSwap || fromToken.toUpperCase() === 'RVN' || toToken.toUpperCase() === 'RVN') {
        aggregatorQuotePromises.push(
          fetchRavencoinBridgeQuote(fromToken, toToken, amount)
        );
      }
      */
      
      // SimpleSwap and ChangeNOW support 1,500+ tokens across 50+ blockchains (UTXO + EVM)
      // Query them for ALL swaps - let them return unavailable if pair not supported
      // SimpleSwap supports BTC, ETH, USDT, USDC, RVN, XRP, SOL, XMR, LTC, DOGE, ICP, HYPE, and hundreds more
      // ChangeNOW supports 600+ tokens across 110+ blockchains (requires VPS proxy)
      aggregatorQuotePromises.push(
        fetchSimpleSwapQuote(fromToken, toToken, amount, destinationAddress, fromTokenChainId, toTokenChainId) // ✅ ACTIVE - Supports ALL tokens
      );
      
      // Skip ChangeNOW for TON swaps - ChangeNOW has strict address validation that requires properly checksummed TON addresses
      // SimpleSwap works fine for TON, so we use that as the primary provider
      if (fromToken.toUpperCase() !== 'TON' && toToken.toUpperCase() !== 'TON') {
        aggregatorQuotePromises.push(
          fetchChangeNOWQuote(fromToken, toToken, amount, destinationAddress, fromTokenChainId, toTokenChainId) // ✅ ACTIVE - Requires VPS proxy (fixed)
        );
      }
      
      const aggregatorQuotes = await Promise.allSettled(aggregatorQuotePromises);

      // Log all quotes for debugging
      console.log('[swapRouter] All quote results:', {
        total: aggregatorQuotes.length,
        successful: aggregatorQuotes.filter(r => r.status === 'fulfilled' && r.value.available).length,
        failed: aggregatorQuotes.filter(r => r.status === 'rejected').length,
        unavailable: aggregatorQuotes.filter(r => r.status === 'fulfilled' && !r.value.available).length,
      });

      // Log each quote result
      aggregatorQuotes.forEach((result, index) => {
        if (result.status === 'fulfilled') {
          console.log(`[swapRouter] Quote ${index}:`, {
            provider: result.value.provider,
            available: result.value.available,
            output: result.value.estimatedOutput,
            error: result.value.error,
          });
        } else {
          console.warn(`[swapRouter] Quote ${index} failed:`, result.reason);
        }
      });

      // Convert ALL quotes (both available and unavailable) to RouterQuote
      // This allows users to see all options, even if some failed
      const allAggregatorQuotes: RouterQuote[] = aggregatorQuotes
        .filter((result): result is PromiseFulfilledResult<SwapQuote> => 
          result.status === 'fulfilled'
        )
        .map(result => ({
          ...result.value,
          source: 'aggregator' as const,
          fromChainId: fromChainId,
          toChainId: toChainId,
        }));

      // Filter only available quotes for best quote selection
      const availableQuotes = allAggregatorQuotes.filter(q => q.available);

      // Add ALL quotes (available + unavailable) so users can see all options
      allQuotes.push(...allAggregatorQuotes);
      
      // Check for minimum amount errors in failed quotes
      const minAmountErrors = aggregatorQuotes
        .filter((result): result is PromiseFulfilledResult<SwapQuote> => 
          result.status === 'fulfilled' && !result.value.available && result.value.minAmount !== undefined
        )
        .map(result => ({
          provider: result.value.provider,
          minAmount: result.value.minAmount!,
          error: result.value.error || 'Minimum amount required',
        }));
      
      // If we have minimum amount errors and no available quotes, show them
      if (minAmountErrors.length > 0 && availableQuotes.length === 0) {
        const minAmountError = minAmountErrors[0]; // Show first minimum amount error
        toast.error('Swap Amount Too Small', {
          description: `${minAmountError.provider} requires a minimum of ${minAmountError.minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}. Please increase the swap amount.`,
          duration: 10000,
        });
      }
    }

    // Filter all available quotes from all sources
    const allAvailableQuotes = allQuotes.filter(q => q.available);

    if (allAvailableQuotes.length === 0) {
      // Check if we have minimum amount errors to show
      const minAmountErrors = allQuotes
        .filter(q => !q.available && q.minAmount !== undefined)
        .map(q => ({ provider: q.provider, minAmount: q.minAmount!, error: q.error || '' }));
      
      if (minAmountErrors.length > 0) {
        const minAmountError = minAmountErrors[0];
        toast.error('Swap Amount Too Small', {
          description: `${minAmountError.provider} requires a minimum of ${minAmountError.minAmount.toFixed(8)} ${fromToken}. Your amount: ${amount} ${fromToken}. Please increase the swap amount.`,
          duration: 10000,
        });
      } else {
        toast.error('No Liquidity Available', {
          description: isCrossChain 
            ? 'No cross-chain liquidity found. The tokens may not be supported for cross-chain swaps.'
            : 'No liquidity found from any provider. Please try again later.',
          duration: 6000,
        });
      }
      return {
        best: null,
        // IMPORTANT: still return all quotes (including unavailable) so the UI can show
        // which providers were tried and why they failed.
        all: allQuotes,
        mm2Available: false,
      };
    }

    // Select best quote (highest estimated output)
    const bestQuote = allAvailableQuotes.reduce((best, current) =>
      current.estimatedOutput > best.estimatedOutput ? current : best
    );

    // Show info about routing (only if multiple options)
    if (allAvailableQuotes.length > 1) {
      toast.info('Multiple Routes Available', {
        description: `Best: ${bestQuote.provider} (${(bestQuote.fee * 100).toFixed(2)}% fee) - ${allAvailableQuotes.length} options found`,
        duration: 4000,
      });
    } else {
      toast.info('Route Found', {
        description: `Using ${bestQuote.provider} (${(bestQuote.fee * 100).toFixed(2)}% fee)`,
        duration: 4000,
      });
    }

    return {
      best: bestQuote,
      all: allQuotes,
      mm2Available: false,
    };
  } catch (error: any) {
    console.error('Router error:', error);
    toast.error('Routing Failed', {
      description: 'Failed to find optimal swap route. Please try again.',
      duration: 5000,
    });
    return {
      best: null,
      all: [],
      mm2Available: false,
    };
  }
}

/**
 * Get best quote only (backward compatibility)
 */
export async function getBestSwapQuote(
  fromToken: string,
  toToken: string,
  amount: number,
  chainId: number = 1
): Promise<RouterQuote | null> {
  const result = await getOptimalSwapRoute(fromToken, toToken, amount, chainId);
  return result.best;
}

/**
 * Execute swap through the selected route
 */
export async function executeSwapRoute(
  quote: RouterQuote,
  fromToken: string,
  toToken: string,
  amount: number,
  walletAddress: string,
  chainId: number = 1,
  destinationAddress?: string
): Promise<{ txHash: string; success: boolean; uuid?: string; depositAddress?: string; exchangeId?: string }> {
  // MM2 Execution - PAUSED (can be re-enabled in future)
  if (quote.source === 'mm2') {
    // MM2 swaps are currently disabled
    toast.error('MM2 Atomic Swaps Disabled', {
      description: 'MM2 atomic swaps are currently paused. Please use aggregator-based swaps instead.',
      duration: 6000,
    });
    return {
      txHash: '',
      success: false,
    };
    /*
    // Execute atomic swap via MM2
    try {
      // Validate MM2 is still available
      const runtime = getMM2RuntimeState();
      if (!isMM2RouteAvailable()) {
        throw new Error('MM2 node is no longer available. Please check your connection.');
      }

      toast.info('Initiating Atomic Swap', {
        description: 'Starting trustless P2P swap via MM2...',
        duration: 4000,
      });

      const result = await executeMM2Swap(fromToken, toToken, amount);
      
      if (!result || !result.uuid) {
        throw new Error('MM2 swap initiated but no UUID returned');
      }

      // Monitor swap status (non-blocking)
      monitorMM2Swap(result.uuid).catch((error) => {
        console.error('Failed to monitor MM2 swap:', error);
        toast.warning('Swap Monitoring Error', {
          description: 'Swap was initiated but status monitoring failed. Check MM2 wallet manager for updates.',
          duration: 8000,
        });
      });

      toast.success('Swap Initiated', {
        description: `Atomic swap ${result.uuid.substring(0, 8)}... started. Monitoring progress...`,
        duration: 6000,
      });

      return {
        txHash: result.uuid,
        success: true,
        uuid: result.uuid,
      };
    } catch (error: any) {
      console.error('MM2 swap execution failed:', error);
      
      // Provide more specific error messages
      let errorMessage = 'Failed to execute MM2 atomic swap';
      if (error.message) {
        if (error.message.includes('No liquidity')) {
          errorMessage = 'No liquidity available for this pair on MM2';
        } else if (error.message.includes('connection') || error.message.includes('network')) {
          errorMessage = 'Cannot connect to MM2 node. Check your connection settings.';
        } else if (error.message.includes('balance') || error.message.includes('insufficient')) {
          errorMessage = 'Insufficient balance in MM2 wallet for this swap';
        } else if (error.message.includes('enabled')) {
          errorMessage = 'One or both coins are not enabled in your MM2 wallet';
        } else {
          errorMessage = error.message;
        }
      }

      toast.error('Atomic Swap Failed', {
        description: errorMessage,
        duration: 8000,
      });
      return {
        txHash: '',
        success: false,
      };
    }
    */
  } else {
    // Execute via aggregator
    try {
      toast.info('Executing Swap', {
        description: `Submitting transaction via ${quote.provider}...`,
        duration: 4000,
      });

      // For cross-chain swaps, use destinationAddress if provided, otherwise use walletAddress
      // Also check if fromChainId !== toChainId as fallback (more reliable than isCrossChain flag)
      const isCrossChainSwap = quote.isCrossChain || (quote.fromChainId && quote.toChainId && quote.fromChainId !== quote.toChainId);
      const recipientAddress = (isCrossChainSwap && destinationAddress) 
        ? destinationAddress 
        : walletAddress;

      // Manual swap providers (SimpleSwap, StealthEX, ChangeNOW) need a real destination address.
      // If user didn't enter one but the destination is an EVM token and the connected walletAddress is a valid EVM address,
      // default to walletAddress to prevent unnecessary "Destination address required" failures.
      let resolvedManualDestinationAddress: string | undefined = destinationAddress;
      if (quote.provider === 'SimpleSwap' || quote.provider === 'StealthEX' || quote.provider === 'ChangeNOW') {
        const { isPlaceholderAddress, isEVMCompatibleToken, isValidEVMAddress } = await import('./liquidityProviders');

        const candidate =
          destinationAddress?.trim() ||
          (isEVMCompatibleToken(toToken, quote.toChainId || chainId) && isValidEVMAddress(walletAddress)
            ? walletAddress
            : '');

        if (!candidate) {
          throw new Error(`Destination address is required for ${quote.provider} swaps`);
        }

        if (isPlaceholderAddress(candidate, toToken)) {
          throw new Error(
            'Please enter a real destination address. Placeholder addresses are only used for quote estimation and cannot be used for actual swaps.'
          );
        }

        resolvedManualDestinationAddress = candidate;
      }

      // For manual swap providers (SimpleSwap, StealthEX, ChangeNOW), always use destinationAddress
      // For other providers, use recipientAddress (which may be walletAddress or destinationAddress)
      const finalDestinationAddress = (quote.provider === 'SimpleSwap' || quote.provider === 'StealthEX' || quote.provider === 'ChangeNOW')
        ? resolvedManualDestinationAddress
        : recipientAddress;

      const result = await executeProviderSwap(
        quote.provider,
        fromToken,
        toToken,
        amount,
        walletAddress,
        quote.fromChainId || chainId,
        quote.toChainId || chainId,
        finalDestinationAddress
      );

      // Validate manual provider responses to ensure we are getting real deposit addresses + IDs
      if (quote.provider === 'SimpleSwap' || quote.provider === 'StealthEX' || quote.provider === 'ChangeNOW') {
        const deposit = (result.depositAddress || '').trim();
        const exchangeId = (result.exchangeId || result.txHash || '').trim();

        if (!exchangeId || exchangeId.length < 6) {
          throw new Error(`${quote.provider} did not return a valid transaction/exchange ID`);
        }

        if (!deposit || deposit.length < 10) {
          throw new Error(`${quote.provider} did not return a deposit address for ${fromToken}`);
        }

        // Reject placeholder deposit addresses (defense-in-depth)
        const { isPlaceholderAddress } = await import('./liquidityProviders');
        if (isPlaceholderAddress(deposit, fromToken)) {
          throw new Error(`${quote.provider} returned a placeholder deposit address (refusing to proceed)`);
        }

        // Basic format validation for common UTXO deposits (BTC/LTC/DOGE/RVN/KAS)
        const upper = fromToken.toUpperCase();
        const isValidDeposit =
          upper === 'BTC'
            ? (/^1[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(deposit) || /^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(deposit) || /^bc1[a-z0-9]{13,62}$/i.test(deposit))
            : upper === 'LTC'
              ? (/^[LM][a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(deposit) || /^3[a-km-zA-HJ-NP-Z1-9]{25,34}$/.test(deposit) || /^ltc1[a-z0-9]{13,62}$/i.test(deposit))
              : upper === 'DOGE'
                ? /^D[a-km-zA-HJ-NP-Z1-9]{33}$/.test(deposit)
                : upper === 'RVN'
                  ? /^R[1-9A-HJ-NP-Za-km-z]{25,39}$/.test(deposit)
                  : upper === 'KAS'
                    ? /^kaspa:[a-z0-9]{40,80}$/i.test(deposit) // Kaspa addresses use bech32 with "kaspa:" prefix
                    : true; // Unknown token: skip strict validation

        if (!isValidDeposit) {
          throw new Error(`${quote.provider} returned an invalid ${fromToken} deposit address format`);
        }
      }

      // Log deposit address for debugging
      console.log('[swapRouter] Swap execution result:', {
        provider: quote.provider,
        txHash: result.txHash,
        success: result.success,
        depositAddress: result.depositAddress,
        exchangeId: result.exchangeId,
        fromToken,
        toToken,
        amount,
      });

      // Monitor transaction status (non-blocking)
      monitorAggregatorSwap(result.txHash, quote.provider).catch((error) => {
        console.error('Failed to monitor aggregator swap:', error);
      });

      return {
        txHash: result.txHash,
        success: result.success,
        depositAddress: result.depositAddress,
        exchangeId: result.exchangeId,
      };
    } catch (error: any) {
      console.error('Aggregator swap execution failed:', error);
      
      // Handle KYC errors specifically
      if (error.message && error.message.includes('KYC_REQUIRED')) {
        toast.error('KYC Verification Required', {
          description: 'This transaction requires KYC verification. You can cancel this swap and try a different provider.',
          duration: 8000,
        });
      } else if (error.message && error.message.includes('Destination address is required')) {
        toast.error('Destination Address Required', {
          description: 'Please enter a destination address for this cross-chain swap.',
          duration: 6000,
        });
      }
      
      return {
        txHash: '',
        success: false,
      };
    }
  }
}

/**
 * Monitor MM2 swap status and notify user when complete
 * PAUSED - can be re-enabled in future
 */
/*
async function monitorMM2Swap(uuid: string, maxAttempts: number = 60): Promise<void> {
  let attempts = 0;
  
  const checkStatus = async (): Promise<void> => {
    if (attempts >= maxAttempts) {
      toast.warning('Swap Status Check Timeout', {
        description: 'Swap is still processing. Please check manually.',
        duration: 8000,
      });
      return;
    }

    try {
      const status = await getMM2SwapStatus(uuid);
      
      if (status.status === 'finished') {
        toast.success('Swap Completed', {
          description: `Atomic swap ${uuid.substring(0, 8)}... completed successfully!`,
          duration: 8000,
        });
        return;
      } else if (status.status === 'refunded') {
        toast.error('Swap Refunded', {
          description: `Swap ${uuid.substring(0, 8)}... was refunded.`,
          duration: 8000,
        });
        return;
      } else if (status.status === 'failed') {
        toast.error('Swap Failed', {
          description: status.error || `Swap ${uuid.substring(0, 8)}... failed.`,
          duration: 8000,
        });
        return;
      } else if (status.status === 'ongoing' || status.status === 'matched') {
        // Still processing, check again in 5 seconds
        attempts++;
        setTimeout(checkStatus, 5000);
      }
    } catch (error: any) {
      console.error('Failed to check MM2 swap status:', error);
      // Retry after 5 seconds
      attempts++;
      setTimeout(checkStatus, 5000);
    }
  };

  // Start monitoring after initial delay
  setTimeout(checkStatus, 5000);
}
*/

/**
 * Monitor aggregator swap transaction status
 */
async function monitorAggregatorSwap(
  txHash: string,
  provider: string,
  maxAttempts: number = 60
): Promise<void> {
  let attempts = 0;
  
  const checkStatus = async (): Promise<void> => {
    if (attempts >= maxAttempts) {
      toast.warning('Transaction Status Check Timeout', {
        description: 'Transaction is still pending. Please check on Etherscan.',
        duration: 8000,
      });
      return;
    }

    try {
      // Check transaction status via Ethereum RPC (using chain registry)
      const { getChainRPCUrl } = await import('./chainRegistry');
      const rpcUrl = getChainRPCUrl('ethereum') || 'https://eth.llamarpc.com';
      const response = await fetch(rpcUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          jsonrpc: '2.0',
          method: 'eth_getTransactionReceipt',
          params: [txHash],
          id: 1,
        }),
      });

      const data = await response.json();

      if (data.result) {
        const receipt = data.result;
        if (receipt.status === '0x1') {
          // Transaction successful
          toast.success('Swap Completed', {
            description: `Transaction confirmed via ${provider}`,
            duration: 8000,
          });
          return;
        } else if (receipt.status === '0x0') {
          // Transaction failed
          toast.error('Swap Failed', {
            description: `Transaction failed on blockchain`,
            duration: 8000,
          });
          return;
        }
      } else {
        // Transaction still pending, check again in 5 seconds
        attempts++;
        setTimeout(checkStatus, 5000);
      }
    } catch (error: any) {
      console.error('Failed to check transaction status:', error);
      // Retry after 5 seconds
      attempts++;
      setTimeout(checkStatus, 5000);
    }
  };

  // Start monitoring after initial delay
  setTimeout(checkStatus, 5000);
}

